import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError } from "@/lib/api";
import { ADMIN_NAV } from "./nav";

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setSettings(data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", {
        price_initial_enrollment: Number(settings.price_initial_enrollment),
        price_annual_reenrollment: Number(settings.price_annual_reenrollment),
        price_records_review: Number(settings.price_records_review),
        price_partner_plan: Number(settings.price_partner_plan),
        price_partner_plus_plan: Number(settings.price_partner_plus_plan),
        price_partner_pro_plan: Number(settings.price_partner_pro_plan),
        passing_grade: Number(settings.passing_grade),
        required_subjects: settings.required_subjects.filter((s) => s.trim()),
        administrator_name: settings.administrator_name,
        administrator_title: settings.administrator_title,
        administrator_signature: settings.administrator_signature,
        acknowledgment_text: settings.acknowledgment_text,
        visitor_alerts_enabled: !!settings.visitor_alerts_enabled,
        partner_activity_alerts_enabled: !!settings.partner_activity_alerts_enabled,
        visitor_alert_email: settings.visitor_alert_email,
      });
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!settings)
    return (
      <AppShell nav={ADMIN_NAV} title="Settings">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </AppShell>
    );

  const set = (key, value) => setSettings({ ...settings, [key]: value });

  return (
    <AppShell
      nav={ADMIN_NAV}
      title="Settings"
      subtitle="Pricing, academic requirements, and document signature"
      actions={
        <Button className="gap-2" onClick={save} disabled={busy} data-testid="save-settings-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          settings
        </Button>
      }
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-slate-200">
          <CardContent className="space-y-5 p-6">
            <h2 className="text-base font-semibold text-slate-900">Pricing</h2>
            {[
              ["price_initial_enrollment", "Initial family enrollment ($)"],
              ["price_annual_reenrollment", "Annual family re-enrollment ($)"],
              ["price_records_review", "Annual records review, per student ($)"],
            ].map(([key, label]) => (
              <div key={key} className="space-y-2">
                <Label>{label}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={settings[key]}
                  onChange={(e) => set(key, e.target.value)}
                  data-testid={`setting-${key}`}
                />
              </div>
            ))}
            <p className="text-xs text-slate-500">
              Saved prices are synced to Stripe automatically for new checkouts.
            </p>
            <div className="border-t border-slate-100 pt-5">
              <h3 className="text-sm font-semibold text-slate-900">
                Partner annual registration
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                Each plan's student limit is fixed: Partner 250, Partner Plus 1,000, Partner Pro
                5,000. Above 5,000 partners must contact you for enterprise pricing.
              </p>
              <div className="mt-4 space-y-4">
                {[
                  ["price_partner_plan", "Partner — up to 250 students ($/year)"],
                  ["price_partner_plus_plan", "Partner Plus — up to 1,000 students ($/year)"],
                  ["price_partner_pro_plan", "Partner Pro — up to 5,000 students ($/year)"],
                ].map(([key, label]) => (
                  <div key={key} className="space-y-2">
                    <Label>{label}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={settings[key] ?? ""}
                      onChange={(e) => set(key, e.target.value)}
                      data-testid={`setting-${key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="space-y-5 p-6">
            <h2 className="text-base font-semibold text-slate-900">Academic review</h2>
            <div className="space-y-2">
              <Label>Minimum passing grade (%)</Label>
              <Input
                type="number"
                step="1"
                value={settings.passing_grade}
                onChange={(e) => set("passing_grade", e.target.value)}
                data-testid="setting-passing-grade"
              />
            </div>
            <div className="space-y-3">
              <Label>Required core subjects</Label>
              {settings.required_subjects.map((subject, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    value={subject}
                    onChange={(e) =>
                      set(
                        "required_subjects",
                        settings.required_subjects.map((s, idx) => (idx === i ? e.target.value : s))
                      )
                    }
                    data-testid={`required-subject-${i}`}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600"
                    onClick={() =>
                      set(
                        "required_subjects",
                        settings.required_subjects.filter((_, idx) => idx !== i)
                      )
                    }
                    data-testid={`remove-required-subject-${i}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => set("required_subjects", [...settings.required_subjects, ""])}
                data-testid="add-required-subject"
              >
                <Plus className="h-4 w-4" /> Add subject
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="space-y-5 p-6">
            <h2 className="text-base font-semibold text-slate-900">Document signature</h2>
            {[
              ["administrator_name", "Administrator name"],
              ["administrator_title", "Administrator title"],
              ["administrator_signature", "Signature text"],
            ].map(([key, label]) => (
              <div key={key} className="space-y-2">
                <Label>{label}</Label>
                <Input
                  value={settings[key]}
                  onChange={(e) => set(key, e.target.value)}
                  data-testid={`setting-${key}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="space-y-5 p-6">
            <h2 className="text-base font-semibold text-slate-900">Visitor alerts</h2>
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="visitor-alerts">Email me about enrollment activity</Label>
                <p className="mt-1 text-xs text-slate-500">
                  One email per visitor when they land on an enrollment page, reach Stripe
                  checkout, and pay.
                </p>
              </div>
              <Switch
                id="visitor-alerts"
                checked={!!settings.visitor_alerts_enabled}
                onCheckedChange={(v) => set("visitor_alerts_enabled", v)}
                data-testid="setting-visitor-alerts-enabled"
              />
            </div>
            <div className="flex items-start justify-between gap-4 border-t border-slate-100 pt-4">
              <div>
                <Label htmlFor="partner-alerts">Email me about partner activity</Label>
                <p className="mt-1 text-xs text-slate-500">
                  When a partner signs in, a student is added under them, or they upload academic
                  records. At most one email per partner per activity every 10 minutes.
                </p>
              </div>
              <Switch
                id="partner-alerts"
                checked={!!settings.partner_activity_alerts_enabled}
                onCheckedChange={(v) => set("partner_activity_alerts_enabled", v)}
                data-testid="setting-partner-activity-alerts"
              />
            </div>
            <div className="space-y-2">
              <Label>Send alerts to</Label>
              <Input
                type="email"
                value={settings.visitor_alert_email || ""}
                onChange={(e) => set("visitor_alert_email", e.target.value)}
                data-testid="setting-visitor-alert-email"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="space-y-4 p-6">
            <h2 className="text-base font-semibold text-slate-900">
              Out-of-state acknowledgment text
            </h2>
            <Textarea
              rows={9}
              value={settings.acknowledgment_text}
              onChange={(e) => set("acknowledgment_text", e.target.value)}
              data-testid="setting-acknowledgment-text"
            />
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
